<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  templatePayload: {
    type: Object,
    default: null,
  },
  inboxId: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n();
const getWhatsAppTemplates = useMapGetter('inboxes/getWhatsAppTemplates');

const buttonComponents = computed(() => {
  const payload = props.templatePayload;
  const components = payload?.components ?? [];
  return components.filter(c => (c.type || '').toLowerCase() === 'button');
});

const templateDefinition = computed(() => {
  const payload = props.templatePayload;
  const inboxId = props.inboxId;
  if (!payload?.name || !inboxId) return null;
  const templates = getWhatsAppTemplates.value(inboxId) ?? [];
  const lang = (payload.language || payload.lang_code || '').toLowerCase();
  return (
    templates.find(
      tmpl =>
        (tmpl.name || tmpl.template_name) === payload.name &&
        (tmpl.language || '').toLowerCase() === lang
    ) ?? null
  );
});

const templateButtons = computed(() => {
  const def = templateDefinition.value;
  if (!def?.components) return [];
  const buttonsComp = def.components.find(
    c => (c.type || '').toLowerCase() === 'buttons'
  );
  return buttonsComp?.buttons ?? [];
});

const buttonsWithLabels = computed(() => {
  return buttonComponents.value.map(comp => {
    const subType = (comp.sub_type || comp.subType || '').toLowerCase();
    const index = parseInt(comp.index ?? comp.index, 10);
    const param = comp.parameters?.[0];
    const paramText = param?.text ?? '';
    const templateBtn = Number.isFinite(index)
      ? templateButtons.value[index]
      : null;
    const label =
      templateBtn?.text ??
      templateBtn?.title ??
      getFallbackLabel(subType, paramText);
    const url =
      subType === 'url' ? paramText || templateBtn?.url || null : null;
    const phone = subType === 'phone_number' ? paramText : null;
    const couponCode = (subType === 'copy_code' && param?.coupon_code) || null;

    return {
      subType,
      label,
      url,
      phone,
      couponCode,
    };
  });
});

function getFallbackLabel(subType, paramText) {
  switch (subType) {
    case 'quick_reply':
      return t('CONVERSATION.WHATSAPP_BUTTON_QUICK_REPLY');
    case 'flow':
      return t('CONVERSATION.WHATSAPP_BUTTON_FLOW');
    case 'url':
      return paramText || t('CONVERSATION.WHATSAPP_BUTTON_LINK');
    case 'phone_number':
      return paramText || t('CONVERSATION.WHATSAPP_BUTTON_LINK');
    case 'copy_code':
      return t('CONVERSATION.WHATSAPP_BUTTON_COPY_CODE');
    default:
      return paramText || t('CONVERSATION.WHATSAPP_BUTTON_LINK');
  }
}

const hasButtons = computed(() => buttonsWithLabels.value.length > 0);
</script>

<template>
  <div v-if="hasButtons" class="flex flex-wrap gap-2 mt-2">
    <template v-for="(btn, i) in buttonsWithLabels" :key="i">
      <a
        v-if="btn.url"
        :href="btn.url"
        target="_blank"
        rel="noreferrer noopener nofollow"
        class="inline-flex items-center rounded-lg border border-n-container bg-n-solid-3 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-3"
      >
        {{ btn.label }}
      </a>
      <a
        v-else-if="btn.phone"
        :href="'tel:' + btn.phone"
        class="inline-flex items-center rounded-lg border border-n-container bg-n-solid-3 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-3"
      >
        {{ btn.label }}
      </a>
      <span
        v-else
        class="inline-flex items-center rounded-lg border border-n-container bg-n-alpha-2 px-3 py-1.5 text-sm text-n-slate-12"
      >
        {{ btn.couponCode ? `${btn.label}: ${btn.couponCode}` : btn.label }}
      </span>
    </template>
  </div>
</template>
